import type { CompetitionDetail } from '../types'

/**
 * The seven setup steps, in order. `slots` is groups for a single-day
 * competition and rounds for a long-running one: the two never coexist.
 */
export const SETUP_STEPS = [
  'about',
  'when',
  'entry',
  'categories',
  'slots',
  'routes',
  'publish',
] as const
export type SetupStep = (typeof SETUP_STEPS)[number]

/** The first three are one form; the new-competition page shows only these. */
export const FORM_STEPS = ['about', 'when', 'entry'] as const

export const STAGES = ['setup', 'live', 'after'] as const
export type Stage = (typeof STAGES)[number]

export const LIVE_TABS = ['competitors', 'results', 'scorecard'] as const
export type LiveTab = (typeof LIVE_TABS)[number]

export const AFTER_TABS = ['final', 'review'] as const
export type AfterTab = (typeof AFTER_TABS)[number]

/**
 * Whether a step counts as done, for its tick and the "3 of 7" count.
 *
 * Groups and rounds are optional, but an empty step is only done once the gym
 * has moved past it (routes added, or published). Ticking it before anyone
 * looked would say it was set up when nobody had seen it.
 */
export function stepDone(step: SetupStep, c: CompetitionDetail): boolean {
  switch (step) {
    case 'about':
    case 'when':
    case 'entry':
      // The competition exists, and the Worker refuses one without these.
      return true
    case 'categories':
      return c.categories.length > 0
    case 'slots':
      return (
        (c.format === 'single_day' ? c.sessions : c.rounds).length > 0 ||
        c.routes.length > 0 ||
        c.published
      )
    case 'routes':
      return c.routes.length > 0
    case 'publish':
      return c.published
  }
}

/** The steps that still block publishing: nobody can enter without a category. */
export function missingForPublish(c: CompetitionDetail): SetupStep[] {
  return (['categories', 'routes'] as const).filter((s) => !stepDone(s, c))
}

/**
 * Where a link from before the redesign lands. `?tab=` used to pick one of
 * eight flat tabs; each now lives under a stage.
 */
export function legacyTab(
  tab: string | null,
): { stage: Stage; step?: SetupStep; live?: LiveTab; after?: AfterTab } | null {
  switch (tab) {
    case 'categories':
      return { stage: 'setup', step: 'categories' }
    case 'groups':
    case 'rounds':
      return { stage: 'setup', step: 'slots' }
    case 'routes':
      return { stage: 'setup', step: 'routes' }
    case 'competitors':
      return { stage: 'live', live: 'competitors' }
    case 'results':
      return { stage: 'live', live: 'results' }
    case 'scorecard':
      return { stage: 'live', live: 'scorecard' }
    case 'review':
      return { stage: 'after', after: 'review' }
    default:
      return null
  }
}
